import { useEffect, useRef, useState } from "react";
import { toast as toastApi } from "../../stores/toast";
import styles from "./UndoToast.module.css";

/**
 * Stacked toast system with enter/exit animations.
 * Call `showUndoToast(...)` from anywhere.
 */

interface ToastState {
	id: number;
	message: string;
	onUndo?: () => void | Promise<void>;
	timeoutMs: number;
	createdAt: number;
}

type Listener = (toasts: ToastState[]) => void;

let currentId = 0;
let toasts: ToastState[] = [];
const listeners = new Set<Listener>();

function emit() {
	for (const l of listeners) l([...toasts]);
}

export function showUndoToast(
	message: string,
	onUndo?: () => void | Promise<void>,
	timeoutMs = 5000,
) {
	currentId += 1;
	toasts = [
		...toasts,
		{ id: currentId, message, onUndo, timeoutMs, createdAt: Date.now() },
	];
	emit();
}

function removeToast(id: number) {
	toasts = toasts.filter((t) => t.id !== id);
	emit();
}

export function dismissToast(id?: number) {
	if (id === undefined) toasts = [];
	else toasts = toasts.filter((t) => t.id !== id);
	emit();
}

/** Individual toast with enter/exit animation phases. */
function ToastItem({ toast, onDone }: { toast: ToastState; onDone: () => void }) {
	const [phase, setPhase] = useState<"entering" | "visible" | "exiting">("entering");
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const exitingRef = useRef(false);

	const startExit = () => {
		if (exitingRef.current) return;
		exitingRef.current = true;
		setPhase("exiting");
		setTimeout(onDone, 250); // match CSS exit duration
	};

	// Enter → visible after animation completes
	useEffect(() => {
		const t = setTimeout(() => setPhase("visible"), 300);
		return () => clearTimeout(t);
	}, []);

	// Auto-dismiss timer
	useEffect(() => {
		if (toast.timeoutMs <= 0) return;
		const elapsed = Date.now() - toast.createdAt;
		const remaining = Math.max(0, toast.timeoutMs - elapsed);
		timerRef.current = setTimeout(startExit, remaining);
		return () => { if (timerRef.current) clearTimeout(timerRef.current); };
	}, [toast.timeoutMs, toast.createdAt]);

	const handleUndo = async () => {
		const undo = toast.onUndo;
		startExit();
		if (!undo) return;
		// The undo is a network call that can fail. Without this catch its
		// rejection was swallowed as an unhandled promise while the toast had
		// already exited — the user saw "undone" over an undo that never
		// landed. Surface the failure instead.
		try {
			await undo();
		} catch (err) {
			toastApi.error(
				"Undo failed",
				err instanceof Error ? err.message : String(err),
			);
		}
	};

	const cls = phase === "entering"
		? `${styles.toast} ${styles.entering}`
		: phase === "exiting"
			? `${styles.toast} ${styles.exiting}`
			: styles.toast;

	return (
		<div role="alert" className={cls}>
			<span className={styles.message}>{toast.message}</span>
			{toast.onUndo && (
				<button
					type="button"
					className={styles.undoBtn}
					onClick={handleUndo}
					aria-label={`Undo: ${toast.message}`}
				>
					Undo
				</button>
			)}
			<button
				type="button"
				className={styles.closeBtn}
				onClick={startExit}
				aria-label="Dismiss notification"
			>
				×
			</button>
		</div>
	);
}

export function UndoToastHost() {
	const [stack, setStack] = useState<ToastState[]>(toasts);

	useEffect(() => {
		const listener: Listener = (t) => setStack(t);
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	}, []);

	if (stack.length === 0) return null;

	return (
		<div
			role="region"
			aria-label="Notifications"
			aria-live="polite"
			className={styles.viewport}
		>
			{stack.map((toast) => (
				<ToastItem
					key={toast.id}
					toast={toast}
					onDone={() => removeToast(toast.id)}
				/>
			))}
		</div>
	);
}
